/**
 * Sound Alert Service
 * Handles unique beep sounds for chat notifications
 */

import { 
  SOUND_CONSTANTS, 
  SOUND_TYPES, 
  SOUND_TAB_NAMES,
  validateSoundType,
  validateTabName
} from '../constants/soundConstants';

class SoundAlertService {
  constructor() {
    this.audioContext = null;
    this.isEnabled = true;
    this.volume = SOUND_CONSTANTS.DEFAULT_VOLUME;
    
    // Track all active timers to prevent memory leaks
    this.activeTimers = new Set();
    
    // Track all active audio nodes to stop them during cleanup
    this.activeAudioNodes = new Set();
    
    // Track if service is being destroyed (prevents new sounds during cleanup)
    this.isDestroyed = false;
    
    // Audio Context Recovery & Health Monitoring
    this.recoveryAttempts = 0;
    this.consecutiveFailures = 0;
    this.isRecovering = false; // Lock to prevent concurrent recovery attempts
    this.recoveryTimer = null;
    this.healthCheckTimer = null;
    this.lastRecoveryAttempt = 0;
    this.contextStateChangeHandler = null;
    this.isAudioDisabled = false; // Flag to disable audio after too many failures
    
    // Per-tab sound settings
    this.tabSettings = {
      alerts: { enabled: true, volume: SOUND_CONSTANTS.DEFAULT_ALERTS_VOLUME },
      inbox: { enabled: true, volume: SOUND_CONSTANTS.DEFAULT_TAB_VOLUME },
      my: { enabled: true, volume: SOUND_CONSTANTS.DEFAULT_TAB_VOLUME },
      all: { enabled: false, volume: SOUND_CONSTANTS.DEFAULT_TAB_VOLUME },
      pin: { enabled: false, volume: SOUND_CONSTANTS.DEFAULT_TAB_VOLUME }
    };
    
    // Cross-tab synchronization
    this.broadcastChannel = null;
    this.storageEventListener = null;
    this.isCrossTabSyncInitialized = false;
    
    // Unique tab identifier to distinguish our own broadcasts
    this.tabId = `tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.lastBroadcastTimestamp = 0;
    
    // Event listeners for React Context integration
    this.listeners = new Map();
    
    // Load settings from localStorage
    this.loadSettings();

    // Initialize audio context
    this.initAudioContext();

    // Safari/WebKit (macOS/iOS) only unlocks a suspended AudioContext when resume() is
    // called synchronously inside a genuine user-gesture handler — resuming later from a
    // timer, a 'statechange' listener, or a websocket-driven call (as the alarm does) is
    // silently ignored. Chrome doesn't have this restriction, which is why the beep works
    // there but not on Mac. Listening for the page's first real gestures and resuming from
    // directly inside them unlocks the context for all later, non-gesture playback.
    this.setupGestureUnlock();

    // Note: Page unload cleanup is handled by React context (SoundSettingsContext)
    // to ensure proper cleanup on component unmount
  }

  // Resume the audio context from directly inside real user-gesture events, so Safari
  // treats it as unlocked. Kept attached (not removed after first use) because Safari can
  // also auto-suspend the context again after periods of inactivity/tab backgrounding.
  setupGestureUnlock() {
    if (typeof document === 'undefined') {
      return;
    }

    const resumeIfSuspended = () => {
      if (this.audioContext && this.audioContext.state === 'suspended') {
        this.audioContext.resume().catch(() => {});
      }
    };

    this.gestureUnlockEvents = ['click', 'touchstart', 'keydown', 'pointerdown'];
    this.gestureUnlockHandler = resumeIfSuspended;
    this.gestureUnlockEvents.forEach((eventName) => {
      document.addEventListener(eventName, this.gestureUnlockHandler, { passive: true });
    });
  }

  // Detach the gesture-unlock listeners added in setupGestureUnlock().
  teardownGestureUnlock() {
    if (typeof document === 'undefined' || !this.gestureUnlockHandler) {
      return;
    }
    (this.gestureUnlockEvents || []).forEach((eventName) => {
      document.removeEventListener(eventName, this.gestureUnlockHandler);
    });
    this.gestureUnlockHandler = null;
    this.gestureUnlockEvents = null;
  }

  // Initialize Web Audio API context with comprehensive lifecycle management
  initAudioContext() {
    // Don't initialize if destroyed or audio is disabled
    if (this.isDestroyed || this.isAudioDisabled) {
      return;
    }

    try {
      // Close existing context if present (prevent multiple contexts)
      if (this.audioContext && this.audioContext.state !== 'closed') {
        // Remove old state change listener before closing
        if (this.contextStateChangeHandler) {
          this.audioContext.removeEventListener('statechange', this.contextStateChangeHandler);
          this.contextStateChangeHandler = null;
        }
        
        this.audioContext.close().catch((err) => {
          console.warn('Error closing existing audio context:', err);
        });
      }
      
      // Create new audio context
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Set up comprehensive state change handler
      if (this.audioContext) {
        this.contextStateChangeHandler = () => {
          this.handleAudioContextStateChange();
        };
        this.audioContext.addEventListener('statechange', this.contextStateChangeHandler);
        
        // Reset failure counters on successful initialization
        this.consecutiveFailures = 0;
        this.recoveryAttempts = 0;
        this.isAudioDisabled = false;
        
        // Start health monitoring
        this.startHealthMonitoring();
      }
    } catch (error) {
      console.error('Web Audio API initialization failed:', error);
      this.audioContext = null;
      this.handleRecoveryFailure();
    }
  }
  
  // Handle audio context state changes with automatic recovery
  handleAudioContextStateChange() {
    if (!this.audioContext || this.isDestroyed) {
      return;
    }

    const state = this.audioContext.state;

    switch (state) {
      case 'suspended':
        // Context is suspended - attempt automatic recovery
        console.warn('Audio context suspended, attempting recovery...');
        this.attemptContextRecovery();
        break;
        
      case 'interrupted':
        // Context was interrupted - attempt recovery
        console.warn('Audio context interrupted, attempting recovery...');
        this.attemptContextRecovery();
        break;
        
      case 'running':
        // Context is healthy - reset failure counters
        this.consecutiveFailures = 0;
        this.recoveryAttempts = 0;
        this.isRecovering = false;
        if (this.recoveryTimer) {
          clearTimeout(this.recoveryTimer);
          this.recoveryTimer = null;
        }
        break;
        
      case 'closed':
        // Context was closed - reinitialize if not destroyed
        if (!this.isDestroyed && !this.isAudioDisabled) {
          console.warn('Audio context closed unexpectedly, reinitializing...');
          this.initAudioContext();
        }
        break;
        
      default:
        console.warn(`Unknown audio context state: ${state}`);
    }
  }
  
  // Attempt to recover audio context with exponential backoff retry
  async attemptContextRecovery() {
    // Prevent concurrent recovery attempts
    if (this.isRecovering || this.isDestroyed || this.isAudioDisabled) {
      return;
    }

    // Check if we've exceeded max attempts
    if (this.recoveryAttempts >= SOUND_CONSTANTS.MAX_RECOVERY_ATTEMPTS) {
      console.error('Maximum recovery attempts reached, disabling audio');
      this.isAudioDisabled = true;
      this.emit('audio-disabled', { reason: 'max-recovery-attempts' });
      return;
    }

    // Check cooldown period
    const now = Date.now();
    const timeSinceLastAttempt = now - this.lastRecoveryAttempt;
    if (timeSinceLastAttempt < SOUND_CONSTANTS.RECOVERY_COOLDOWN_MS) {
      const remainingCooldown = SOUND_CONSTANTS.RECOVERY_COOLDOWN_MS - timeSinceLastAttempt;
      this.scheduleRecoveryAttempt(remainingCooldown);
      return;
    }

    this.isRecovering = true;
    this.recoveryAttempts++;
    this.lastRecoveryAttempt = now;

    try {
      // If context doesn't exist, reinitialize
      if (!this.audioContext || this.audioContext.state === 'closed') {
        this.initAudioContext();
        this.isRecovering = false;
        return;
      }

      // Attempt to resume suspended/interrupted context
      if (this.audioContext.state === 'suspended' || this.audioContext.state === 'interrupted') {
        const resumePromise = this.audioContext.resume();
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Resume timeout')), SOUND_CONSTANTS.CONTEXT_RESUME_TIMEOUT_MS);
        });

        await Promise.race([resumePromise, timeoutPromise]);
        
        // Verify context is now running
        if (this.audioContext.state === 'running') {
          this.consecutiveFailures = 0;
          this.recoveryAttempts = 0;
          this.isRecovering = false;
          this.emit('audio-recovered', { attempts: this.recoveryAttempts });
        } else {
          throw new Error(`Context state is ${this.audioContext.state}, expected 'running'`);
        }
      } else if (this.audioContext.state === 'running') {
        this.consecutiveFailures = 0;
        this.recoveryAttempts = 0;
        this.isRecovering = false;
      }
    } catch (error) {
      console.error(`Recovery attempt ${this.recoveryAttempts} failed:`, error);
      this.consecutiveFailures++;
      this.isRecovering = false;

      // If too many consecutive failures, disable audio
      if (this.consecutiveFailures >= SOUND_CONSTANTS.MAX_CONSECUTIVE_FAILURES) {
        console.error('Too many consecutive failures, disabling audio');
        this.isAudioDisabled = true;
        this.emit('audio-disabled', { reason: 'consecutive-failures', failures: this.consecutiveFailures });
        return;
      }

      // Schedule retry with exponential backoff
      const delay = Math.min(
        SOUND_CONSTANTS.RECOVERY_INITIAL_DELAY_MS * Math.pow(SOUND_CONSTANTS.RECOVERY_BACKOFF_MULTIPLIER, this.recoveryAttempts - 1),
        SOUND_CONSTANTS.RECOVERY_MAX_DELAY_MS
      );
      
      this.scheduleRecoveryAttempt(delay);
    }
  }
  
  // Schedule a recovery attempt with delay
  scheduleRecoveryAttempt(delay) {
    if (this.recoveryTimer) {
      clearTimeout(this.recoveryTimer);
    }
    
    this.recoveryTimer = this.scheduleTimer(() => {
      this.recoveryTimer = null;
      if (!this.isDestroyed && !this.isAudioDisabled) {
        this.attemptContextRecovery();
      }
    }, delay);
  }
  
  // Start periodic health monitoring
  startHealthMonitoring() {
    // Clear existing health check timer
    if (this.healthCheckTimer) {
      clearTimeout(this.healthCheckTimer);
    }

    // Don't start monitoring if destroyed or disabled
    if (this.isDestroyed || this.isAudioDisabled) {
      return;
    }

    this.healthCheckTimer = this.scheduleTimer(() => {
      this.performHealthCheck();
      // Schedule next health check
      this.startHealthMonitoring();
    }, SOUND_CONSTANTS.CONTEXT_HEALTH_CHECK_INTERVAL_MS);
  }
  
  // Perform health check on audio context
  performHealthCheck() {
    if (this.isDestroyed || this.isAudioDisabled) {
      return;
    }

    try {
      if (!this.audioContext) {
        console.warn('Health check: Audio context is null, reinitializing...');
        this.initAudioContext();
        return;
      }

      const state = this.audioContext.state;
      
      if (state === 'closed') {
        console.warn('Health check: Audio context is closed, reinitializing...');
        this.initAudioContext();
      } else if (state === 'suspended' || state === 'interrupted') {
        console.warn(`Health check: Audio context is ${state}, attempting recovery...`);
        this.attemptContextRecovery();
      } else if (state === 'running') {
        // Context is healthy - reset failure counters
        this.consecutiveFailures = 0;
        this.recoveryAttempts = 0;
      }
    } catch (error) {
      console.error('Health check failed:', error);
      this.handleRecoveryFailure();
    }
  }
  
  // Handle recovery failure
  handleRecoveryFailure() {
    this.consecutiveFailures++;
    
    if (this.consecutiveFailures >= SOUND_CONSTANTS.MAX_CONSECUTIVE_FAILURES) {
      console.error('Too many recovery failures, disabling audio');
      this.isAudioDisabled = true;
      this.emit('audio-disabled', { reason: 'recovery-failures', failures: this.consecutiveFailures });
    }
  }
  
  // Manually re-enable audio after it's been disabled
  reEnableAudio() {
    if (this.isDestroyed) {
      return;
    }

    this.isAudioDisabled = false;
    this.consecutiveFailures = 0;
    this.recoveryAttempts = 0;
    this.isRecovering = false;
    
    // Reinitialize context
    this.initAudioContext();
    
    this.emit('audio-enabled', {});
  }
  
  // Check if audio context is valid and ready
  isAudioContextValid() {
    return (
      this.audioContext &&
      this.audioContext.state !== 'closed' &&
      !this.isDestroyed &&
      !this.isAudioDisabled
    );
  }
  
  // Safely schedule a timer with tracking
  // Fixed race condition: checks if timer is still in Set before executing
  scheduleTimer(callback, delay) {
    if (this.isDestroyed) {
      return null;
    }
    
    const timerId = setTimeout(() => {
      // Check if timer was cancelled BEFORE removing from Set
      // This prevents race condition where cancelAllSounds() is called
      // but the timer callback was already queued by the browser
      if (!this.activeTimers.has(timerId)) {
        // Timer was cancelled, don't execute
        return;
      }
      
      // Remove from active timers when executed
      this.activeTimers.delete(timerId);
      
      // Only execute if service is still valid
      if (!this.isDestroyed && this.isAudioContextValid()) {
        try {
          callback();
        } catch (error) {
          console.error('Error in scheduled sound callback:', error);
        }
      }
    }, delay);
    
    // Track the timer
    this.activeTimers.add(timerId);
    return timerId;
  }

  // Load settings from localStorage
  loadSettings() {
    try {
      const settings = JSON.parse(localStorage.getItem('soundAlertSettings') || '{}');
      this.isEnabled = settings.isEnabled !== undefined ? settings.isEnabled : true;
      this.volume = settings.volume !== undefined ? settings.volume : SOUND_CONSTANTS.DEFAULT_VOLUME;
      
      // Load per-tab settings
      if (settings.tabSettings) {
        this.tabSettings = { ...this.tabSettings, ...settings.tabSettings };
      }
    } catch (error) {
      console.error('Error loading sound settings:', error);
    }
  }

  // Helper method to handle settings updates from cross-tab sync
  handleSettingsUpdate() {
    try {
      this.loadSettings();
      this.emit('settings-changed', this.getSettings());
    } catch (error) {
      console.error('Error handling settings update:', error);
      // Fallback: emit with current settings to ensure UI updates
      try {
        this.emit('settings-changed', this.getSettings());
      } catch (emitError) {
        console.error('Error emitting settings-changed event:', emitError);
      }
    }
  }

  // Initialize cross-tab synchronization (lazy initialization)
  initCrossTabSync() {
    if (this.isCrossTabSyncInitialized || this.isDestroyed) {
      return;
    }

    try {
      // Initialize BroadcastChannel for modern browsers (same-origin tabs)
      if (typeof BroadcastChannel !== 'undefined') {
        this.broadcastChannel = new BroadcastChannel('sound-settings-sync');
        
        this.broadcastChannel.onmessage = (event) => {
          // Ignore messages from the same tab (we already updated)
          if (event.data && 
              event.data.type === 'settings-updated' && 
              event.data.tabId !== this.tabId) {
            this.handleSettingsUpdate();
          }
        };
      }

      // Fallback: Use storage event for older browsers or cross-origin scenarios
      // Note: storage event only fires in OTHER tabs, not the current tab
      this.storageEventListener = (e) => {
        if (e.key === 'soundAlertSettings' && e.newValue !== null) {
          this.handleSettingsUpdate();
        }
      };

      window.addEventListener('storage', this.storageEventListener);
      this.isCrossTabSyncInitialized = true;
    } catch (error) {
      console.warn('Error initializing cross-tab synchronization:', error);
    }
  }

  // Cleanup cross-tab synchronization
  cleanupCrossTabSync() {
    if (this.broadcastChannel) {
      try {
        this.broadcastChannel.close();
      } catch (error) {
        console.warn('Error closing BroadcastChannel:', error);
      }
      this.broadcastChannel = null;
    }

    if (this.storageEventListener) {
      window.removeEventListener('storage', this.storageEventListener);
      this.storageEventListener = null;
    }

    this.isCrossTabSyncInitialized = false;
  }

  // Event emitter pattern for React Context integration
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  // Remove event listener
  off(event, callback) {
    if (!this.listeners.has(event)) {
      return;
    }
    const callbacks = this.listeners.get(event);
    const index = callbacks.indexOf(callback);
    if (index > -1) {
      callbacks.splice(index, 1);
    }
  }

  // Emit event to all listeners
  emit(event, data) {
    if (!this.listeners.has(event)) {
      return;
    }
    this.listeners.get(event).forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in event listener for "${event}":`, error);
      }
    });
  }

  // Save settings to localStorage
  saveSettings() {
    try {
      const settings = {
        isEnabled: this.isEnabled,
        volume: this.volume,
        tabSettings: this.tabSettings
      };
      localStorage.setItem('soundAlertSettings', JSON.stringify(settings));
      
      // Initialize cross-tab sync if not already done (lazy initialization)
      if (!this.isCrossTabSyncInitialized) {
        this.initCrossTabSync();
      }
      
      // Broadcast to other tabs via BroadcastChannel (works for same-tab and other tabs)
      if (this.broadcastChannel) {
        try {
          const timestamp = Date.now();
          this.lastBroadcastTimestamp = timestamp;
          this.broadcastChannel.postMessage({
            type: 'settings-updated',
            tabId: this.tabId,
            timestamp: timestamp
          });
        } catch (error) {
          console.warn('Error broadcasting settings update:', error);
        }
      }
      
      // Note: storage event is automatically fired by browser for other tabs
      // but NOT for the current tab, which is why we use BroadcastChannel
    } catch (error) {
      console.error('Error saving sound settings:', error);
    }
  }

  // Update settings
  updateSettings(newSettings) {
    this.isEnabled = newSettings.isEnabled !== undefined ? newSettings.isEnabled : this.isEnabled;
    this.volume = newSettings.volume !== undefined ? newSettings.volume : this.volume;
    
    // Update per-tab settings if provided
    if (newSettings.tabSettings) {
      this.tabSettings = { ...this.tabSettings, ...newSettings.tabSettings };
    }
    
    this.saveSettings();
    
    // Emit event for current tab's listeners (React Context)
    // Note: Other tabs will receive the broadcast and emit their own events
    this.emit('settings-changed', this.getSettings());
  }

  // Get current settings
  getSettings() {
    return {
      isEnabled: this.isEnabled,
      volume: this.volume,
      tabSettings: this.tabSettings
    };
  }

  // Generate unique beep sounds using Web Audio API
  generateBeep(frequency, duration, type = 'sine', volume = null, intensity = SOUND_CONSTANTS.DEFAULT_INTENSITY) {
    // Validate audio context before proceeding
    if (!this.isAudioContextValid() || !this.isEnabled || this.isDestroyed) {
      return;
    }

    // Set volume (use provided volume or default) with intensity multiplier
    const finalVolume = volume !== null ? volume : this.volume;
    
    // Don't play sound if volume is zero
    if (finalVolume <= SOUND_CONSTANTS.MIN_VOLUME) {
      return;
    }
    
    const loudness = finalVolume * SOUND_CONSTANTS.BASE_VOLUME_MULTIPLIER * intensity;
    
    // Don't play if calculated loudness is too low
    if (loudness <= SOUND_CONSTANTS.MIN_LOUDNESS_THRESHOLD) {
      return;
    }

    try {
      // Double-check audio context is still valid (race condition protection)
      if (!this.isAudioContextValid()) {
        return;
      }
      
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();
      
      // Track audio nodes for cleanup
      this.activeAudioNodes.add(oscillator);
      this.activeAudioNodes.add(gainNode);
      
      // Cleanup function to remove nodes from tracking when sound ends
      const cleanupNodes = () => {
        this.activeAudioNodes.delete(oscillator);
        this.activeAudioNodes.delete(gainNode);
      };
      
      // Remove nodes from tracking when oscillator ends
      oscillator.onended = cleanupNodes;
      
      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      oscillator.frequency.setValueAtTime(frequency, this.audioContext.currentTime);
      oscillator.type = type;
      
      gainNode.gain.setValueAtTime(SOUND_CONSTANTS.MIN_VOLUME, this.audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(loudness, this.audioContext.currentTime + SOUND_CONSTANTS.GAIN_RAMP_START_TIME);
      gainNode.gain.exponentialRampToValueAtTime(SOUND_CONSTANTS.GAIN_RAMP_END_VALUE, this.audioContext.currentTime + duration);
      
      const startTime = this.audioContext.currentTime;
      const stopTime = startTime + duration;
      
      oscillator.start(startTime);
      oscillator.stop(stopTime);
    } catch (error) {
      console.error('Error generating beep:', error);
    }
  }

  // Play different types of notification sounds with enhanced error recovery
  // durationOverride (seconds) is only honored by sound types that support a variable
  // ring length (e.g. the Alerted Conversation Alarm, driven by the socket's ring_seconds).
  playNotificationSound(type = SOUND_TYPES.NEW_MESSAGE, tabName = null, durationOverride = null) {
    // Validate and normalize input parameters
    type = validateSoundType(type, SOUND_TYPES.NEW_MESSAGE);
    tabName = validateTabName(tabName);

    // Early return if service is destroyed, disabled, or audio is disabled
    if (this.isDestroyed || !this.isEnabled || this.isAudioDisabled) {
      return;
    }

    // Ensure audio context is valid and ready
    if (!this.isAudioContextValid()) {
      if (this.isDestroyed || this.isAudioDisabled) {
        return;
      }
      // Attempt to initialize or recover context
      this.initAudioContext();
      if (!this.isAudioContextValid()) {
        // If still invalid, attempt recovery (fire-and-forget)
        this.attemptContextRecovery().catch((error) => {
          console.error('Background recovery attempt failed:', error);
        });
        return;
      }
    }

    // Check if sound is enabled for the specific tab
    if (tabName && !this.isTabSoundEnabled(tabName)) {
      return;
    }

    // Use tab-specific volume if available
    const volume = tabName ? this.getTabVolume(tabName) : this.volume;

    // Check if volume is zero - don't play sound if volume is 0
    if (volume <= SOUND_CONSTANTS.MIN_VOLUME) {
      return;
    }

    // Handle suspended/interrupted context with proper recovery
    const contextState = this.audioContext.state;
    if (contextState === 'suspended' || contextState === 'interrupted') {
      // Use the recovery mechanism instead of direct resume
      this.attemptContextRecovery()
        .then(() => {
          // After recovery, verify context is ready before playing
          if (this.isAudioContextValid() && this.audioContext.state === 'running') {
            this.playSoundInternal(type, volume, tabName, durationOverride);
          }
        })
        .catch((error) => {
          console.error('Failed to recover audio context for playback:', error);
          // Don't play sound if recovery failed
        });
      return;
    }

    // Play sound immediately if context is already running
    if (contextState === 'running') {
      this.playSoundInternal(type, volume, tabName, durationOverride);
    } else {
      // Unknown state - attempt recovery (fire-and-forget)
      this.attemptContextRecovery().catch((error) => {
        console.error('Recovery attempt for unknown state failed:', error);
      });
    }
  }

  // Internal method to play sounds (called after context is ready)
  playSoundInternal(type, volume, tabName, durationOverride = null) {
    // Validate type again (defensive programming)
    type = validateSoundType(type, SOUND_TYPES.NEW_MESSAGE);

    // Determine intensity based on tab
    const intensity = tabName === SOUND_TAB_NAMES.ALERTS
      ? SOUND_CONSTANTS.ALERTS_INTENSITY_MULTIPLIER
      : SOUND_CONSTANTS.DEFAULT_INTENSITY;

      console.log('sound', intensity, volume, tabName, type);

    switch (type) {
      case SOUND_TYPES.NEW_CONVERSATION:
        this.playNewConversationSound(volume, intensity);
        break;
      case SOUND_TYPES.NEW_MESSAGE:
        this.playNewMessageSound(volume, intensity);
        break;
      case SOUND_TYPES.URGENT:
        this.playUrgentSound(volume, intensity);
        break;
      case SOUND_TYPES.REVIEW_ALARM:
        this.playReviewAlarmSound(volume, intensity);
        break;
      case SOUND_TYPES.ALERTS_ALARM_SINGLE:
        this.playAlertsAlarmSingleSound(volume, intensity, durationOverride);
        break;
      case SOUND_TYPES.ALERTS_ALARM_MULTIPLE:
        this.playAlertsAlarmMultipleSound(volume, intensity, durationOverride);
        break;
      default:
        // Fallback to new message sound (should never reach here due to validation)
        this.playNewMessageSound(volume, intensity);
    }
  }

  // New conversation sound - distinctive ascending beep
  playNewConversationSound(volume = null, intensity = SOUND_CONSTANTS.DEFAULT_INTENSITY) {
    if (this.isDestroyed || !this.isAudioContextValid()) {
      return;
    }
    
    const frequencies = SOUND_CONSTANTS.NEW_CONVERSATION_FREQS;
    const duration = SOUND_CONSTANTS.NEW_CONVERSATION_DURATION;
    
    frequencies.forEach((freq, index) => {
      const delay = index * SOUND_CONSTANTS.NEW_CONVERSATION_INTERVAL;
      const timerId = this.scheduleTimer(() => {
        this.generateBeep(freq, duration, 'sine', volume, intensity);
      }, delay);
      
      // If timer scheduling failed (service destroyed), stop scheduling
      if (!timerId) {
        return;
      }
    });
  }

  // New message sound - quick double beep
  playNewMessageSound(volume = null, intensity = SOUND_CONSTANTS.DEFAULT_INTENSITY) {
    if (this.isDestroyed || !this.isAudioContextValid()) return;
    
    const frequency = SOUND_CONSTANTS.NEW_MESSAGE_FREQ;
    const duration = SOUND_CONSTANTS.NEW_MESSAGE_DURATION;
    
    // First beep (immediate)
    this.generateBeep(frequency, duration, 'sine', volume, intensity);
    
    // Second beep after short delay (tracked)
    const timerId = this.scheduleTimer(() => {
      this.generateBeep(frequency + SOUND_CONSTANTS.NEW_MESSAGE_FREQ_STEP, duration, 'sine', volume, intensity);
    }, SOUND_CONSTANTS.NEW_MESSAGE_INTERVAL);
    
    // If timer scheduling failed, we already played the first beep, which is acceptable
    if (!timerId) {
      return;
    }
  }

  // Urgent sound - rapid triple beep
  playUrgentSound(volume = null, intensity = SOUND_CONSTANTS.DEFAULT_INTENSITY) {
    if (this.isDestroyed || !this.isAudioContextValid()) return;
    
    const frequency = SOUND_CONSTANTS.URGENT_FREQ;
    const duration = SOUND_CONSTANTS.URGENT_DURATION;
    
    for (let i = 0; i < SOUND_CONSTANTS.URGENT_BEEP_COUNT; i++) {
      const timerId = this.scheduleTimer(() => {
        this.generateBeep(frequency, duration, 'square', volume, intensity);
      }, i * SOUND_CONSTANTS.URGENT_INTERVAL);
      
      // If timer scheduling failed (service destroyed), stop scheduling
      if (!timerId) {
        return;
      }
    }
  }

  // Review-conversation modal alarm - sustained tone (used with a repeating timer by the caller)
  playReviewAlarmSound(volume = null, intensity = SOUND_CONSTANTS.DEFAULT_INTENSITY) {
    if (this.isDestroyed || !this.isAudioContextValid()) return;

    this.generateBeep(
      SOUND_CONSTANTS.REVIEW_ALARM_FREQ,
      SOUND_CONSTANTS.REVIEW_ALARM_DURATION,
      'square',
      volume,
      intensity
    );
  }

  // Clamp a socket-provided ring duration (seconds) to a sane playable range.
  clampAlertsAlarmRingSeconds(durationSeconds) {
    const fallback = SOUND_CONSTANTS.ALERTS_ALARM_DEFAULT_RING_SECONDS;
    const value = Number(durationSeconds);
    if (!Number.isFinite(value) || value <= 0) return fallback;
    return Math.min(
      Math.max(value, SOUND_CONSTANTS.ALERTS_ALARM_MIN_RING_SECONDS),
      SOUND_CONSTANTS.ALERTS_ALARM_MAX_RING_SECONDS
    );
  }

  // Alerted Conversation Alarm - single card ring: a soft two-note "ding-dong" chime,
  // repeated for ring_seconds. Replaces the old single sustained tone, which read as a
  // harsh, unbroken drone rather than a notification.
  playAlertsAlarmSingleSound(volume = null, intensity = SOUND_CONSTANTS.DEFAULT_INTENSITY, ringSeconds = null) {
    if (this.isDestroyed || !this.isAudioContextValid()) return;

    const duration = this.clampAlertsAlarmRingSeconds(ringSeconds);
    const noteDuration = SOUND_CONSTANTS.ALERTS_ALARM_SINGLE_NOTE_DURATION;
    const noteGapMs = SOUND_CONSTANTS.ALERTS_ALARM_SINGLE_NOTE_GAP_MS;
    const chimeIntervalMs = SOUND_CONSTANTS.ALERTS_ALARM_SINGLE_CHIME_INTERVAL_MS;
    const chimeCount = Math.max(1, Math.floor((duration * 1000) / chimeIntervalMs));

    for (let i = 0; i < chimeCount; i++) {
      const chimeStart = i * chimeIntervalMs;

      const lowTimerId = this.scheduleTimer(() => {
        this.generateBeep(SOUND_CONSTANTS.ALERTS_ALARM_SINGLE_FREQ_LOW, noteDuration, 'sine', volume, intensity);
      }, chimeStart);
      if (!lowTimerId) return;

      const highTimerId = this.scheduleTimer(() => {
        this.generateBeep(SOUND_CONSTANTS.ALERTS_ALARM_SINGLE_FREQ_HIGH, noteDuration, 'sine', volume, intensity);
      }, chimeStart + noteGapMs);
      if (!highTimerId) return;
    }
  }

  // Alerted Conversation Alarm - multiple cards ring: alternating two-tone pulses for ring_seconds,
  // so it's audibly distinct from the single-card sustained tone.
  playAlertsAlarmMultipleSound(volume = null, intensity = SOUND_CONSTANTS.DEFAULT_INTENSITY, ringSeconds = null) {
    if (this.isDestroyed || !this.isAudioContextValid()) return;

    const duration = this.clampAlertsAlarmRingSeconds(ringSeconds);
    const pulseDuration = SOUND_CONSTANTS.ALERTS_ALARM_MULTIPLE_PULSE_DURATION;
    const pulseIntervalMs = SOUND_CONSTANTS.ALERTS_ALARM_MULTIPLE_PULSE_INTERVAL_MS;
    const pulseCount = Math.max(1, Math.floor((duration * 1000) / pulseIntervalMs));

    for (let i = 0; i < pulseCount; i++) {
      const frequency = i % 2 === 0
        ? SOUND_CONSTANTS.ALERTS_ALARM_MULTIPLE_FREQ_LOW
        : SOUND_CONSTANTS.ALERTS_ALARM_MULTIPLE_FREQ_HIGH;
      const timerId = this.scheduleTimer(() => {
        this.generateBeep(frequency, pulseDuration, 'square', volume, intensity);
      }, i * pulseIntervalMs);

      // If timer scheduling failed (service destroyed), stop scheduling further pulses.
      if (!timerId) return;
    }
  }

  // Test sound function
  testSound(type = SOUND_TYPES.NEW_MESSAGE, tabName = null) {
    // Validation is handled by playNotificationSound
    this.playNotificationSound(type, tabName);
  }

  // Tab-specific sound management methods
  isTabSoundEnabled(tabName) {
    return this.tabSettings[tabName]?.enabled || false;
  }

  getTabVolume(tabName) {
    // Return the tab's specific volume, or global volume if tab volume is not set
    const tabVolume = this.tabSettings[tabName]?.volume;
    return tabVolume !== undefined ? tabVolume : this.volume;
  }

  updateTabSettings(tabName, settings) {
    if (this.tabSettings[tabName]) {
      this.tabSettings[tabName] = { ...this.tabSettings[tabName], ...settings };
      this.saveSettings();
      // Emit event for current tab's listeners (React Context)
      this.emit('settings-changed', this.getSettings());
    }
  }

  toggleTabSound(tabName) {
    if (this.tabSettings[tabName]) {
      this.tabSettings[tabName].enabled = !this.tabSettings[tabName].enabled;
      this.saveSettings();
      // Emit event for current tab's listeners (React Context)
      this.emit('settings-changed', this.getSettings());
    }
  }

  setTabVolume(tabName, volume) {
    if (this.tabSettings[tabName]) {
      this.tabSettings[tabName].volume = Math.max(SOUND_CONSTANTS.MIN_VOLUME, Math.min(SOUND_CONSTANTS.MAX_VOLUME, volume));
      this.saveSettings();
      // Emit event for current tab's listeners (React Context)
      this.emit('settings-changed', this.getSettings());
    }
  }

  getTabSettings(tabName) {
    return this.tabSettings[tabName] || { enabled: false, volume: SOUND_CONSTANTS.FALLBACK_TAB_VOLUME };
  }

  getAllTabSettings() {
    return this.tabSettings;
  }
  
  // Cancel all pending sound timers and stop active audio nodes
  cancelAllSounds() {
    // Early return if already destroyed (avoid redundant operations)
    if (this.isDestroyed && this.activeTimers.size === 0 && this.activeAudioNodes.size === 0) {
      return;
    }

    // Cancel all pending timers
    this.activeTimers.forEach((timerId) => {
      try {
        clearTimeout(timerId);
      } catch (error) {
        console.warn('Error clearing timer:', error);
      }
    });
    this.activeTimers.clear();
    
    // Stop all active audio nodes immediately
    this.activeAudioNodes.forEach((node) => {
      try {
        // Stop oscillator nodes
        if (node.stop && typeof node.stop === 'function') {
          node.stop();
        }
        // Disconnect gain nodes and oscillators
        if (node.disconnect && typeof node.disconnect === 'function') {
          node.disconnect();
        }
      } catch (error) {
        // Ignore errors from nodes that are already stopped/disconnected
        // This is expected when nodes finish naturally
      }
    });
    this.activeAudioNodes.clear();
  }
  
  // Get count of active timers (useful for debugging)
  getActiveTimerCount() {
    return this.activeTimers.size;
  }
  
  // Get count of active audio nodes (useful for debugging)
  getActiveAudioNodeCount() {
    return this.activeAudioNodes.size;
  }
  
  // Cleanup method - call this when app is shutting down
  // This should only be called on actual page unload, not on component unmounts
  destroy() {
    // Prevent multiple destroy calls
    if (this.isDestroyed) {
      return;
    }

    // Mark as destroyed to prevent new sounds
    this.isDestroyed = true;
    
    // Stop health monitoring
    if (this.healthCheckTimer) {
      clearTimeout(this.healthCheckTimer);
      this.healthCheckTimer = null;
    }
    
    // Stop recovery attempts
    if (this.recoveryTimer) {
      clearTimeout(this.recoveryTimer);
      this.recoveryTimer = null;
    }
    
    // Cancel all pending timers and stop active audio nodes
    this.cancelAllSounds();

    // Cleanup cross-tab synchronization
    this.cleanupCrossTabSync();

    // Remove gesture-unlock listeners
    this.teardownGestureUnlock();
    
    // Remove state change listener before closing
    if (this.audioContext && this.contextStateChangeHandler) {
      try {
        this.audioContext.removeEventListener('statechange', this.contextStateChangeHandler);
      } catch (error) {
        console.warn('Error removing state change listener:', error);
      }
      this.contextStateChangeHandler = null;
    }
    
    // Close audio context
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().catch((error) => {
        console.warn('Error closing audio context during destroy:', error);
      });
    }
    
    this.audioContext = null;
    
    // Clear all references to help with garbage collection
    this.activeTimers.clear();
    this.activeAudioNodes.clear();
    this.listeners.clear();
    
    // Reset recovery state
    this.recoveryAttempts = 0;
    this.consecutiveFailures = 0;
    this.isRecovering = false;
    this.isAudioDisabled = false;
  }

}

// Create singleton instance
const soundAlertService = new SoundAlertService();

export default soundAlertService;
